import type { Http } from './http';
import type {
  BillingSettings,
  CompanyDetail,
  Company,
  CompanyProfileInput,
  CompanyStatus,
  CompanyUser,
  Dashboard,
  EmailMessage,
  EmailMessageDetail,
  EmailOverview,
  EmailQuery,
  Invoice,
  InvoiceDraft,
  InvoiceStatus,
  IssueInvoiceInput,
  Lang,
  NewCompanyUserInput,
  Paged,
  PagedQuery,
  PaymentInput,
  Plan,
  PlanChoice,
  PlanInput,
  PlatformLogin,
  PlatformRole,
  PlatformUser,
  Subscription,
  TrialChoice,
} from './saas';

/** Where the platform trades a refresh token (createHttp's `refreshPath`). */
export const PLATFORM_REFRESH_PATH = '/api/v1/platform/auth/refresh';

const B = '/api/v1/platform';

/** Every platform route (docs/SAAS.md §4.3), typed. The http must use PLATFORM_REFRESH_PATH. */
export function createPlatformApi(http: Http) {
  const get = <T>(path: string, query?: object) => http.request<T>('GET', `${B}${path}`, { query: query as never });
  const post = <T>(path: string, body?: unknown) => http.request<T>('POST', `${B}${path}`, { body: body ?? {} });
  const put = <T>(path: string, body?: unknown) => http.request<T>('PUT', `${B}${path}`, { body });
  const patch = <T>(path: string, body?: unknown) => http.request<T>('PATCH', `${B}${path}`, { body });
  const del = <T>(path: string) => http.request<T>('DELETE', `${B}${path}`);

  return {
    auth: {
      login: (username: string, password: string) => http.request<PlatformLogin>('POST', `${B}/auth/login`, { body: { username, password }, auth: false }),
    },
    me: {
      get: () => get<PlatformUser>('/me'),
      update: (body: { fullName: string; email: string; language: Lang }) => put<PlatformUser>('/me', body),
      /** returns fresh tokens: every other session is signed out */
      changePassword: (currentPassword: string, newPassword: string) => put<PlatformLogin>('/me/password', { currentPassword, newPassword }),
    },
    dashboard: () => get<Dashboard>('/dashboard'),
    companies: {
      list: (query: PagedQuery & { search?: string; status?: CompanyStatus | '' } = {}) => get<Paged<Company>>('/companies', query),
      statusCounts: () => get<Record<CompanyStatus, number>>('/companies/status-counts'),
      get: (id: string) => get<CompanyDetail>(`/companies/${id}`),
      create: (body: CompanyProfileInput & { admin?: NewCompanyUserInput | null; subscription?: PlanChoice | null; trial?: TrialChoice | null }) => post<CompanyDetail>('/companies', body),
      update: (id: string, body: CompanyProfileInput) => put<CompanyDetail>(`/companies/${id}`, body),
      /** with a plan, a free trial, both or neither */
      approve: (id: string, plan: PlanChoice | null, trial: TrialChoice | null = null) => post<CompanyDetail>(`/companies/${id}/approve`, { plan, trial }),
      trial: {
        /** gives a free trial, or changes the one running: `days` long from its first day (a new one starts today) */
        set: (id: string, days: number) => put<CompanyDetail>(`/companies/${id}/trial`, { days }),
        /** ends it now: from now on the plan applies — without one, the company's users cannot sign in */
        end: (id: string) => post<CompanyDetail>(`/companies/${id}/trial/end`),
      },
      reject: (id: string, reason: string) => post<CompanyDetail>(`/companies/${id}/reject`, { reason }),
      suspend: (id: string, reason: string) => post<CompanyDetail>(`/companies/${id}/suspend`, { reason }),
      reactivate: (id: string) => post<CompanyDetail>(`/companies/${id}/reactivate`),
      remove: (id: string) => del<void>(`/companies/${id}`),
      users: {
        list: (id: string) => get<CompanyUser[]>(`/companies/${id}/users`),
        create: (id: string, body: NewCompanyUserInput) => post<CompanyUser>(`/companies/${id}/users`, body),
        update: (id: string, userId: number, body: Omit<NewCompanyUserInput, 'username' | 'password'>) => put<CompanyUser>(`/companies/${id}/users/${userId}`, body),
        setStatus: (id: string, userId: number, status: 'ACTIVE' | 'INACTIVE') => patch<CompanyUser>(`/companies/${id}/users/${userId}/status`, { status }),
        setPassword: (id: string, userId: number, password: string) => put<CompanyUser>(`/companies/${id}/users/${userId}/password`, { password }),
      },
      subscription: {
        assign: (id: string, body: PlanChoice) => put<Subscription>(`/companies/${id}/subscription`, body),
        cancel: (id: string, reason?: string | null) => post<Subscription>(`/companies/${id}/subscription/cancel`, { reason: reason ?? null }),
      },
      nextInvoice: (id: string) => get<InvoiceDraft>(`/companies/${id}/invoices/next`),
      issueInvoice: (id: string, body: IssueInvoiceInput) => post<Invoice>(`/companies/${id}/invoices`, body),
    },
    invoices: {
      list: (query: PagedQuery & { companyId?: string; status?: InvoiceStatus | 'OVERDUE' | ''; search?: string } = {}) => get<Paged<Invoice>>('/invoices', query),
      get: (id: string) => get<Invoice>(`/invoices/${id}`),
      pay: (id: string, body: PaymentInput) => post<Invoice>(`/invoices/${id}/pay`, body),
      void: (id: string, reason: string) => post<Invoice>(`/invoices/${id}/void`, { reason }),
      /** The PDF, in the language asked for. */
      async pdf(id: string, lang: Lang) {
        const res = await http.raw('GET', `${B}/invoices/${id}/pdf`, { query: { lang } });
        const disposition = res.headers.get('content-disposition') ?? '';
        return { blob: await res.blob(), filename: /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `invoice-${id}.pdf` };
      },
    },
    plans: {
      list: () => get<Plan[]>('/plans'),
      create: (body: PlanInput) => post<Plan>('/plans', body),
      update: (id: string, body: PlanInput) => put<Plan>(`/plans/${id}`, body),
      remove: (id: string) => del<void>(`/plans/${id}`),
    },
    /** whether the API has a Google Maps key (GOOGLE_MAPS_API_KEY) to give companies Google Maps with */
    maps: () => get<{ googleConfigured: boolean }>('/maps'),
    settings: {
      billing: () => get<BillingSettings>('/settings/billing'),
      saveBilling: (body: BillingSettings) => put<BillingSettings>('/settings/billing', body),
    },
    email: {
      overview: () => get<EmailOverview>('/email'),
      /** ADMIN */
      setEnabled: (enabled: boolean) => put<EmailOverview>('/email', { enabled }),
      /** sends to the signed-in platform user at once and answers with what became of it */
      test: () => post<EmailMessage>('/email/test'),
      messages: (q: EmailQuery) => get<Paged<EmailMessage>>('/email/messages', q),
      message: (id: string) => get<EmailMessageDetail>(`/email/messages/${id}`),
      /** a FAILED or SKIPPED message, queued again */
      resend: (id: string) => post<EmailMessage>(`/email/messages/${id}/resend`),
    },
    users: {
      list: () => get<PlatformUser[]>('/users'),
      create: (body: { username: string; fullName: string; email: string; password: string; role: PlatformRole; language: Lang }) => post<PlatformUser>('/users', body),
      update: (id: string, body: { fullName: string; email: string; role: PlatformRole; language: Lang }) => put<PlatformUser>(`/users/${id}`, body),
      setStatus: (id: string, status: 'ACTIVE' | 'INACTIVE') => patch<PlatformUser>(`/users/${id}/status`, { status }),
      setPassword: (id: string, password: string) => put<PlatformUser>(`/users/${id}/password`, { password }),
    },
  };
}

export type PlatformApi = ReturnType<typeof createPlatformApi>;
